import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  LinearProgress,
  Popover,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Mood as MoodIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Reply as ReplyIcon,
  StickyNote2 as StickyNote2Icon,
} from '@mui/icons-material';

const K = { blue: '#4185F1', navy: '#121826', red: '#E53935', noteBg: '#FFF8C5', noteBorder: '#FFE066', noteText: '#5C4B00' };

const EMOJI_GRID = [
  '😀','😁','😂','🤣','😊','😍','😘','😉','😎','🤩',
  '😅','😇','🙂','🙃','🥰','😋','🤔','🤨','😐','😑',
  '😏','😒','🙄','😔','😪','😴','😢','😭','😤','😠',
  '😡','🥵','🥶','😱','🤯','😨','😰','😥','🤗','🤭',
  '👍','👎','👌','🙏','👏','🙌','🤝','💪','🤞','✌️',
  '❤️','🧡','💛','💚','💙','💜','🖤','💔','💯','🔥',
  '✨','⭐','🎉','🎊','💰','💵','💸','💳','🏦','📌',
  '✅','❌','⚠️','❓','❗','📞','📱','📧','📅','⏰',
];

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

export const MessageComposer = ({
  onSend,
  onUpload,
  disabled = false,
  placeholder = 'Escribe un mensaje…',
  replyTo = null,
  onCancelReply,
}) => {
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(null); // {mediaId, mimeType, mediaClass, filename, sizeBytes, previewUrl}
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [attachAnchor, setAttachAnchor] = useState(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [internalMode, setInternalMode] = useState(false);
  const [error, setError] = useState(null);

  const mediaInputRef = useRef(null);
  const docInputRef = useRef(null);
  const recorderRef = useRef(null);
  const recorderChunksRef = useRef([]);
  const recorderCancelledRef = useRef(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  const insertEmoji = (emoji) => {
    const input = inputRef.current;
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const next = message.slice(0, start) + emoji + message.slice(end);
      setMessage(next);
      requestAnimationFrame(() => {
        input.focus();
        const pos = start + emoji.length;
        input.setSelectionRange(pos, pos);
      });
    } else {
      setMessage((m) => m + emoji);
    }
  };

  useEffect(() => () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
  }, [pending]);

  const resetAfterSend = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setMessage('');
    setPending(null);
    if (onCancelReply) onCancelReply();
  };

  const handleSendClick = () => {
    if (uploading || recording) return;
    if (internalMode) {
      if (!message.trim()) return;
      onSend({ text: message.trim(), replyToId: replyTo?.id, internal: true });
      resetAfterSend();
      return;
    }
    if (pending) {
      onSend({
        mediaId: pending.mediaId,
        type: pending.mediaClass,
        caption: message.trim() || null,
        filename: pending.filename,
        voice: pending.voice,
        replyToId: replyTo?.id,
      });
      resetAfterSend();
    } else if (message.trim()) {
      onSend({ text: message.trim(), replyToId: replyTo?.id });
      resetAfterSend();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    }
  };

  const uploadFile = async (file, type) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const res = await onUpload(file, type);
      const previewUrl = file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      setPending({ ...res, previewUrl });
    } catch (e) {
      setError(e.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const handlePickMedia = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadFile(file);
  };

  const handlePickDoc = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) uploadFile(file, 'document');
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type?.startsWith('image/')) {
        const blob = item.getAsFile();
        if (blob) {
          e.preventDefault();
          uploadFile(blob, 'image');
          return;
        }
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderChunksRef.current = [];
      recorderCancelledRef.current = false;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recorderCancelledRef.current) {
          recorderChunksRef.current = [];
          return;
        }
        const blob = new Blob(recorderChunksRef.current, { type: 'audio/webm' });
        recorderChunksRef.current = [];
        if (blob.size === 0) return;
        const file = new File([blob], 'voice.webm', { type: 'audio/webm' });
        setUploading(true);
        try {
          const res = await onUpload(file, 'voice');
          onSend({
            mediaId: res.mediaId,
            type: 'audio',
            voice: true,
            replyToId: replyTo?.id,
          });
          if (onCancelReply) onCancelReply();
        } catch (e) {
          setError(e.message || 'Error al enviar la nota de voz');
        } finally {
          setUploading(false);
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch (e) {
      setError('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = (cancel = false) => {
    recorderCancelledRef.current = cancel;
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
    clearInterval(timerRef.current);
    timerRef.current = null;
    setRecording(false);
    setRecordingTime(0);
  };

  const removePending = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  const showSendButton = !!message.trim() || !!pending;
  const composerDisabled = disabled || uploading || recording;

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{ position: 'relative' }}
    >
      {dragOver && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(65,133,241,0.10)',
            border: `2px dashed ${K.blue}`,
            borderRadius: 1.5,
            zIndex: 2,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color: K.blue }}>
            Soltá el archivo para adjuntar
          </Typography>
        </Box>
      )}

      {replyTo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.75,
            mb: 0.75,
            borderLeft: `3px solid ${K.blue}`,
            bgcolor: 'rgba(65,133,241,0.06)',
            borderRadius: 0.75,
          }}
        >
          <ReplyIcon fontSize="small" sx={{ color: K.blue }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: K.blue, display: 'block' }}>
              Respondiendo
            </Typography>
            <Typography variant="caption" noWrap sx={{ display: 'block', color: 'rgba(18,24,38,0.7)' }}>
              {replyTo.preview || replyTo.content || 'Mensaje'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancelReply}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {pending && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1,
            mb: 0.75,
            borderRadius: 1,
            bgcolor: 'rgba(0,0,0,0.04)',
          }}
        >
          {pending.previewUrl ? (
            <Box
              component="img"
              src={pending.previewUrl}
              alt=""
              sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 0.75 }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 0.75 }}>
              <FileIcon sx={{ color: K.navy }} />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {pending.filename || pending.mediaClass}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              {pending.mimeType}
            </Typography>
          </Box>
          <IconButton size="small" onClick={removePending}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {uploading && <LinearProgress sx={{ mb: 0.5 }} />}

      {error && (
        <Typography variant="caption" sx={{ color: K.red, display: 'block', mb: 0.5, px: 0.5 }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          p: 1.25,
          borderRadius: 1.5,
          border: internalMode ? `1px solid ${K.noteBorder}` : '1px solid rgba(18, 24, 38, 0.1)',
          backgroundColor: internalMode ? K.noteBg : '#fff',
          boxShadow: '0 1px 3px rgba(18, 24, 38, 0.06)',
          transition: 'background-color .15s, border-color .15s',
        }}
      >
        <input
          ref={mediaInputRef}
          type="file"
          accept="image/*,video/*"
          hidden
          onChange={handlePickMedia}
        />
        <input
          ref={docInputRef}
          type="file"
          hidden
          onChange={handlePickDoc}
        />

        {!recording && !internalMode && (
          <IconButton
            size="small"
            disabled={composerDisabled}
            onClick={(e) => setAttachAnchor(e.currentTarget)}
            sx={{ color: 'rgba(18,24,38,0.45)' }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        )}

        {!recording && (
          <IconButton
            size="small"
            disabled={composerDisabled}
            onClick={() => setInternalMode((v) => !v)}
            title={internalMode ? 'Volver a mensaje' : 'Nota interna'}
            sx={{
              color: internalMode ? K.noteText : 'rgba(18,24,38,0.45)',
              bgcolor: internalMode ? 'rgba(255,224,102,0.5)' : 'transparent',
            }}
          >
            <StickyNote2Icon fontSize="small" />
          </IconButton>
        )}

        <Menu
          anchorEl={attachAnchor}
          open={!!attachAnchor}
          onClose={() => setAttachAnchor(null)}
        >
          <MenuItem onClick={() => { setAttachAnchor(null); mediaInputRef.current?.click(); }}>
            <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Foto o video</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { setAttachAnchor(null); docInputRef.current?.click(); }}>
            <ListItemIcon><FileIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Documento</ListItemText>
          </MenuItem>
        </Menu>

        {recording ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: K.red, animation: 'pulse 1s infinite' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: K.navy, flex: 1 }}>
              Grabando · {formatTime(recordingTime)}
            </Typography>
            <IconButton size="small" onClick={() => stopRecording(true)} sx={{ color: K.red }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            inputRef={inputRef}
            placeholder={internalMode ? 'Nota interna (no se envía al cliente)' : pending ? 'Agregar un comentario…' : placeholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={composerDisabled}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    sx={{ color: 'rgba(18,24,38,0.4)' }}
                    onClick={(e) => setEmojiAnchor(e.currentTarget)}
                  >
                    <MoodIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                py: 0.5,
                fontSize: '0.9375rem',
                color: K.navy,
              },
            }}
          />
          <Popover
            anchorEl={emojiAnchor}
            open={!!emojiAnchor}
            onClose={() => setEmojiAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, 28px)',
              gap: 0.5,
              p: 1,
              maxWidth: 320,
            }}>
              {EMOJI_GRID.map((e) => (
                <Box
                  key={e}
                  onClick={() => insertEmoji(e)}
                  sx={{
                    width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    borderRadius: 0.75,
                    fontSize: '1.125rem',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                  }}
                >
                  {e}
                </Box>
              ))}
            </Box>
          </Popover>
          </>
        )}

        {recording ? (
          <IconButton
            onClick={() => stopRecording(false)}
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              bgcolor: K.red,
              color: '#fff',
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#c62828' },
            }}
          >
            <StopIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : (showSendButton || internalMode) ? (
          <IconButton
            onClick={handleSendClick}
            disabled={composerDisabled || (internalMode && !message.trim())}
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              bgcolor: internalMode ? '#D4A300' : K.blue,
              color: '#fff',
              borderRadius: 1.5,
              '&:hover': { bgcolor: internalMode ? '#B58C00' : '#3574d9' },
              '&.Mui-disabled': {
                bgcolor: internalMode ? 'rgba(212,163,0,0.35)' : 'rgba(65, 133, 241, 0.35)',
                color: '#fff',
              },
            }}
          >
            {uploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        ) : (
          <IconButton
            onClick={startRecording}
            disabled={composerDisabled}
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              bgcolor: K.blue,
              color: '#fff',
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#3574d9' },
            }}
          >
            <MicIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
